import type { Line } from 'd3-shape';
import { curveStepBefore, line } from 'd3-shape';
import { flow, groupBy, map, partialRight, property } from 'lodash-es';
import type { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs';
import type { BaseType } from 'd3-selection';
import type { Graph } from './graph';
import type { SkipFirst } from '../utils/types';
import type { GraphElementPosition } from './graph-elements/base-provider';
import classNames from '../utils/classNames';

interface GroupedData {
    axis: string;
    data: GraphElementPosition[];
}

function createIdentifyingClass(...classes: string[]) {
    const className = classes.join(' ');
    const selector = classes.map((singleClassName) => `.${singleClassName}`).join('');

    return { className, selector };
}

export class GraphType {
    d3IdentifierCssClass: string;

    constructor(d3IdentifierCssClass: string) {
        this.d3IdentifierCssClass = d3IdentifierCssClass;
    }

    getX(data: GraphElementPosition): number {
        return data.x;
    }

    getY(data: GraphElementPosition): number {
        return data.y;
    }

    render(_graph: Graph, _data: GraphElementPosition[]): void {
        throw new Error('render method must be implemented by subclass');
    }

    static createRenderObservable(
        graphElementPositions$: Observable<GraphElementPosition[]>,
        graph: Graph,
        d3IdentifierCssClass: string,
        ...args: SkipFirst<ConstructorParameters<typeof this>>
    ): Observable<GraphElementPosition[]> {
        const renderer = new this(d3IdentifierCssClass, ...args);

        return graphElementPositions$.pipe(
            tap((elements) => {
                renderer.render(graph, elements);
            }),
            finalize(() => {
                // On unsubscribe remove the elements.
                renderer.render(graph, []);
            })
        );
    }
}

export class LineGraphType extends GraphType {
    line(): Line<GraphElementPosition> {
        return line<GraphElementPosition>()
            .x((element) => element.x)
            .y((element) => element.y);
    }

    render(graph: Graph, data: GraphElementPosition[]): void {
        const axes = data;
        const groupedByAxis = flow(
            partialRight(groupBy, 'axis'),
            partialRight(map, (value: string, key: string) => ({ axis: key, data: value }))
        )(axes) as GroupedData[];

        const identifying = createIdentifyingClass('line', this.d3IdentifierCssClass);

        // Render lines.
        graph.drawArea
            .selectAll(identifying.selector)
            .data(groupedByAxis, property('axis'))
            .join(
                (enter) =>
                    enter
                        .append('svg:path')
                        .attr('class', (d) =>
                            classNames('linegraph', d.axis, identifying.className)
                        )
                        .attr('d', (d) => this.line()(d.data)),
                (update) => update.attr('d', (d) => this.line()(d.data))
            );
    }
}

export class StepGraphType extends LineGraphType {
    line(): Line<GraphElementPosition> {
        const lineGenerator = super.line();
        return lineGenerator.curve(curveStepBefore);
    }
}

export class BarGraphType extends GraphType {
    rectWidth: number;

    constructor(d3IdentifierCssClass: string, rectWidth: number) {
        super(d3IdentifierCssClass);
        this.rectWidth = rectWidth;
    }

    getX(data: GraphElementPosition): number {
        // Center rect on value.
        return super.getX(data) - this.rectWidth / 2;
    }

    getWidth(): number {
        return this.rectWidth;
    }

    getHeight(graph: Graph, data: GraphElementPosition): number {
        return graph.graphHeight - this.getY(data);
    }

    render(graph: Graph, data: GraphElementPosition[]): void {
        const identifying = createIdentifyingClass('bar', this.d3IdentifierCssClass);

        graph.drawArea
            .selectAll<BaseType, GraphElementPosition>(identifying.selector)
            .data(data, (d) => `${d.timestamp}-${d.axis}`)
            .join((enter) =>
                enter
                    .append('rect')
                    .attr('class', (d) => classNames('bargraph', d.axis, identifying.className))
                    .attr('width', this.getWidth.bind(this))
            )
            .attr('height', (d) => this.getHeight(graph, d))
            .attr('x', this.getX.bind(this))
            .attr('y', this.getY.bind(this));
    }
}

export class DotGraphType extends BarGraphType {
    getY(data: GraphElementPosition): number {
        // Center rect on value.
        return super.getY(data) - this.rectWidth / 2;
    }

    getHeight(): number {
        return this.rectWidth;
    }
}
